import * as Colors from '@brightlayer-ui/colors';
import { Done, Send } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { FilledTextField } from '../../components/FilledTextField';
import { UserSelectors } from '../../features/userSlice';
import { useAlert } from '../../lib/AlertContext';
import { OrganizationService } from '../../services/organization/organization.service';
import { UserRole } from '../../services/user/types';
import { OrganizationFormValidations } from '../validations';

const styles = {
    sectionHeader: {
        paddingBottom: 2,
    },
    title: {
        color: Colors.blue['500'],
    },
    content: {
        color: Colors.black['500'],
    },
    textAndTitle: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '20px',
    },
    organizationNameForm: {
        display: 'flex',
        flexDirection: 'column',
    },
    textField: {
        maxWidth: '400px',
        color: Colors.white[200],
        width: '100%',
    },
    formHelperText: {
        marginLeft: '0px',
    },
    submitButton: {
        maxWidth: '240px',
    },
    circularProgress: {
        color: 'white',
    },
    container: {
        padding: '24px',
        backgroundColor: Colors.white[50],
        margin: '24px',
    },
    icon: {
        fontSize: 56,
        color: Colors.blue[500],
    },
    helperTextStyle: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
};

export const CreateOrganizationForm: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showErrorToUser, showInfoAlertToUser } = useAlert();
    /*To get the organizations from the Redux store
      This is used to validate duplicate organization names*/
    const organizations = useAppSelector(UserSelectors.userOrganizationRoles);
    const organizationNameArray = organizations
        .filter((org) => org.role === UserRole.OWNER)
        .map((org) => org.organizationName);
    const [isOrganizationNameDuplicateError, setOrganizationNameDuplicateError] = useState(false);

    const [organizationName, setOrganizationName] = useState<string>();
    const [organizationDescription, setOrganizationDescription] = useState<string>();
    const [pointOfContactName, setPointOfContactName] = useState<string>();
    const [pointOfContactEmail, setPointOfContactEmail] = useState<string>();
    const [pointOfContactPhoneNumber, setPointOfContactPhoneNumber] = useState<string>();

    const [organizationNameError, setOrganizationNameError] = useState(false);
    const [organizationDescriptionError, setOrganizationDescriptionError] = useState(false);
    const [pointOfContactNameError, setPointOfContactNameError] = useState(false);
    const [pointOfContactEmailError, setPointOfContactEmailError] = useState(false);
    const [pointOfContactPhoneNumberError, setPointOfContactPhoneNumberError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const submitDisabled =
        !organizationDescription ||
        !pointOfContactName ||
        !pointOfContactEmail ||
        !organizationName ||
        isLoading ||
        organizationNameError ||
        isOrganizationNameDuplicateError ||
        organizationDescriptionError ||
        pointOfContactNameError ||
        pointOfContactEmailError ||
        pointOfContactPhoneNumberError;

    const renderFailedToCreateError = (): void => {
        showErrorToUser({
            errorTitle: t('CREATE_ORGANIZATION_PAGE.FAILED_TO_REQUEST_ORG_TITLE'),
            errorDescription: t('CREATE_ORGANIZATION_PAGE.FAILED_TO_REQUEST_ORG_BODY'),
            onCancel: () => {
                navigate('/organizations');
            },
            confirmText: t('COMMON.TRY_AGAIN'),
            cancelText: t('COMMON.CANCEL'),
            onConfirm: () => {},
        });
    };

    const successInformationMessage = (): void => {
        showInfoAlertToUser({
            title: t('CREATE_ORGANIZATION_PAGE.REQUEST_SENT'),
            description: t('CREATE_ORGANIZATION_PAGE.REQUEST_SENT_DESCRIPTION'),
            icon: <Done style={styles.icon} />,
            onDismiss: () => {
                navigate('/organizations');
            },
        });
    };

    const handleOnClick = async (): Promise<void> => {
        if (organizationName && organizationDescription && pointOfContactName && pointOfContactEmail) {
            setIsLoading(true);

            try {
                await OrganizationService.requestOrganization({
                    name: organizationName,
                    description: organizationDescription,
                    pointOfContact: {
                        name: pointOfContactName,
                        email: pointOfContactEmail,
                        phoneNumber: pointOfContactPhoneNumber,
                    },
                });
                successInformationMessage();
            } catch {
                renderFailedToCreateError();
            } finally {
                setIsLoading(false);
            }
        }
    };

    const validateOrgName = (userInput: string): void => {
        const orgNameValidatorResult = OrganizationFormValidations.organizationNameValidator(userInput);

        const isDuplicateName = OrganizationFormValidations.organizationDuplicateNameValidator(
            userInput,
            organizationNameArray
        );

        setOrganizationNameError(!orgNameValidatorResult);
        setOrganizationNameDuplicateError(isDuplicateName);
    };

    return (
        <Box sx={styles.container}>
            <Box sx={styles.organizationNameForm}>
                <Box sx={styles.sectionHeader}>
                    <Typography variant="h6">{t('CREATE_ORGANIZATION_PAGE.ORG_DETAILS')}</Typography>
                    <Typography variant="caption">{t('CREATE_ORGANIZATION_PAGE.ORG_DETAILS_INSTRUCTIONS')}</Typography>
                </Box>

                <Box>
                    <FilledTextField
                        label={t('CREATE_ORGANIZATION_PAGE.ORG_NAME_LABEL')}
                        data-testid="organization-name-text-box"
                        sx={styles.textField}
                        error={organizationNameError || isOrganizationNameDuplicateError}
                        inputProps={{ maxLength: 256 }}
                        helperText={
                            <Box sx={styles.helperTextStyle}>
                                <span>
                                    {isOrganizationNameDuplicateError
                                        ? t('ERROR.DUPLICATE_ORG_NAME_ERROR')
                                        : organizationNameError
                                        ? t('CREATE_ORGANIZATION_PAGE.ORG_NAME_ERROR')
                                        : undefined}
                                </span>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    {`${organizationName?.length ?? 0}/256`}
                                </Typography>
                            </Box>
                        }
                        required={true}
                        onChange={(event): void => {
                            setOrganizationName(event.target.value);
                            validateOrgName(event.target.value.trim());
                        }}
                        onBlur={(): void => {
                            if (organizationName) {
                                validateOrgName(organizationName.trim());
                            }
                        }}
                    />

                    <FilledTextField
                        label={t('CREATE_ORGANIZATION_PAGE.ORG_DESC_LABEL')}
                        data-testid="organization-description-text-box"
                        sx={{ ...styles.textField, maxWidth: '850px' }}
                        error={organizationDescriptionError}
                        inputProps={{ maxLength: 1024 }}
                        helperText={
                            <Box sx={styles.helperTextStyle}>
                                <span>
                                    {organizationDescriptionError
                                        ? t('CREATE_ORGANIZATION_PAGE.ORG_DESCRIPTION_ERROR')
                                        : undefined}
                                </span>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    {`${organizationDescription?.length ?? 0}/1024`}
                                </Typography>
                            </Box>
                        }
                        required={true}
                        onChange={(event): void => {
                            setOrganizationDescriptionError(false);
                            setOrganizationDescription(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (organizationDescription !== undefined) {
                                setOrganizationDescriptionError(
                                    !OrganizationFormValidations.organizationDescriptionValidator(
                                        organizationDescription.trim()
                                    )
                                );
                            }
                        }}
                    />
                </Box>

                <Box sx={styles.sectionHeader}>
                    <Typography variant="h6">{t('CREATE_ORGANIZATION_PAGE.POC_DETAILS')}</Typography>
                    <Typography variant="caption">{t('CREATE_ORGANIZATION_PAGE.POC_INSTRUCTIONS')}</Typography>
                </Box>

                <Box>
                    <FilledTextField
                        label={t('CREATE_ORGANIZATION_PAGE.POC_NAME_LABEL')}
                        data-testid="point-of-contact-name-text-box"
                        sx={styles.textField}
                        error={pointOfContactNameError}
                        inputProps={{ maxLength: 256 }}
                        helperText={
                            <Box sx={styles.helperTextStyle}>
                                <span>
                                    {pointOfContactNameError ? t('CREATE_ORGANIZATION_PAGE.POC_NAME_ERROR') : undefined}
                                </span>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    {`${pointOfContactName?.length ?? 0}/256`}
                                </Typography>
                            </Box>
                        }
                        required={true}
                        onChange={(event): void => {
                            setPointOfContactNameError(false);
                            setPointOfContactName(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (pointOfContactName !== undefined) {
                                setPointOfContactNameError(
                                    !OrganizationFormValidations.pointOfContactNameValidator(pointOfContactName.trim())
                                );
                            }
                        }}
                    />
                    <FilledTextField
                        label={t('CREATE_ORGANIZATION_PAGE.POC_EMAIL_LABEL')}
                        data-testid="point-of-contact-email-text-box"
                        sx={styles.textField}
                        error={pointOfContactEmailError}
                        helperText={
                            pointOfContactEmailError ? t('CREATE_ORGANIZATION_PAGE.POC_EMAIL_ERROR') : undefined
                        }
                        required={true}
                        onChange={(event): void => {
                            setPointOfContactEmailError(false);
                            setPointOfContactEmail(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (pointOfContactEmail !== undefined) {
                                setPointOfContactEmailError(
                                    !OrganizationFormValidations.pointOfContactEmailValidator(
                                        pointOfContactEmail.trim()
                                    )
                                );
                            }
                        }}
                    />
                    <FilledTextField
                        label={t('CREATE_ORGANIZATION_PAGE.POC_PHONE_LABEL')}
                        data-testid="point-of-contact-phone-number-text-box"
                        sx={styles.textField}
                        error={pointOfContactPhoneNumberError}
                        helperText={
                            pointOfContactPhoneNumberError
                                ? t('CREATE_ORGANIZATION_PAGE.POC_PHONE_NUMBER_ERROR')
                                : undefined
                        }
                        onChange={(event): void => {
                            setPointOfContactPhoneNumberError(false);
                            setPointOfContactPhoneNumber(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (pointOfContactPhoneNumber) {
                                setPointOfContactPhoneNumberError(
                                    !OrganizationFormValidations.pointOfContactPhoneNumberValidator(
                                        pointOfContactPhoneNumber.trim()
                                    )
                                );
                            }
                        }}
                    />
                </Box>

                <Button
                    sx={styles.submitButton}
                    id="submit-button"
                    variant="contained"
                    color="primary"
                    onClick={(): void => void handleOnClick()}
                    disabled={submitDisabled}
                    startIcon={!isLoading && <Send />}
                >
                    {isLoading ? (
                        <CircularProgress sx={styles.circularProgress} size={20} variant={'indeterminate'} />
                    ) : (
                        t('CREATE_ORGANIZATION_PAGE.REQUEST_NEW_ORGANIZATION')
                    )}
                </Button>
            </Box>
        </Box>
    );
};
